import {
  createContext,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DialogProps, Drawer as DrawerPrimitive } from "vaul";
import X24 from "@/icons/__generated__/X/X24";
import { cn } from "@/lib/utils";
import { useDrawerManager } from "./DrawerManager/DrawerManagerContext";
// Define prop types for components
interface DrawerCloseProps extends HTMLAttributes<HTMLButtonElement> {
  className?: string;
  children?: ReactNode;
}

interface DrawerOverlayProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

interface DrawerContentProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  children?: ReactNode;
}

interface DrawerTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  className?: string;
}

interface DrawerDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {
  className?: string;
}

// Define prop types for non-forwardRef components
interface DrawerHeaderProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  showCloseButton?: boolean;
}

interface DrawerFooterProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export type DrawerContextType = {
  id: string;
};

const DrawerContext = createContext<DrawerContextType>({
  id: "",
});

const Drawer = ({ shouldScaleBackground = true, ...props }: DialogProps) => {
  // will be undefined if not wrapped in DrawerManagerProvider, which is allowed
  const drawerManager = useDrawerManager();

  const id = useMemo(() => {
    return Math.random().toString(36).substring(2, 15);
  }, []);

  const [open, setOpen] = useState(props.open ?? false);

  useEffect(() => {
    setOpen(props.open ?? false);
  }, [props.open]);

  return (
    <DrawerContext.Provider value={{ id }}>
      <DrawerPrimitive.Root
        shouldScaleBackground={shouldScaleBackground}
        {...props}
        open={open}
        onOpenChange={(open) => {
          if (props.onOpenChange) {
            props.onOpenChange(open);
          } else {
            setOpen(open);
          }

          if (!drawerManager) {
            return;
          }

          if (open) {
            drawerManager.openDrawer(id);
          } else if (
            drawerManager.drawerStack.length > 1 &&
            !drawerManager.clearAllOnClose
          ) {
            // if this is not the last drawer in the stack, call closeDrawer immediately
            drawerManager.closeDrawer(id);
          }
        }}
        onAnimationEnd={(open) => {
          if (!drawerManager) {
            return;
          }

          // if this is the last drawer in the stack, wait for animation to end before calling closeDrawer
          if (
            !open &&
            (drawerManager.drawerStack.length === 1 ||
              drawerManager.clearAllOnClose)
          ) {
            drawerManager.closeDrawer(id);
          }
        }}
      />
    </DrawerContext.Provider>
  );
};
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = forwardRef<HTMLButtonElement, DrawerCloseProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <DrawerPrimitive.Close className={className} ref={ref} {...props}>
        {children}
      </DrawerPrimitive.Close>
    );
  },
);

DrawerClose.displayName = DrawerPrimitive.Close.displayName;

const DrawerOverlay = forwardRef<HTMLDivElement, DrawerOverlayProps>(
  ({ className, ...props }, ref) => {
    const { id } = useContext(DrawerContext);
    const drawerManager = useDrawerManager();
    const isBottomDrawer = drawerManager?.isBottomDrawer(id);

    return (
      <DrawerPrimitive.Overlay
        ref={ref}
        className={cn(
          "tw-fixed tw-inset-0 tw-bg-overlay-dark tw-z-[1031]",
          { "!tw-opacity-0": !!drawerManager && !isBottomDrawer },
          className,
        )}
        {...props}
      />
    );
  },
);
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = forwardRef<HTMLDivElement, DrawerContentProps>(
  ({ className, children, ...props }, ref) => {
    const { id } = useContext(DrawerContext);
    const drawerManager = useDrawerManager();
    const isTopDrawer = drawerManager?.isTopDrawer(id);

    return (
      <DrawerPortal>
        <DrawerOverlay />
        <DrawerPrimitive.Content
          ref={ref}
          className={cn(
            "tw-fixed tw-bottom-0 tw-inset-x-0 tw-mt-24 tw-flex tw-h-auto tw-flex-col tw-rounded-t-12 tw-rounded-b-0 tw-bg-base-white tw-z-[1031]",
            { "tw-hidden": !!drawerManager && !isTopDrawer },
            className,
          )}
          {...props}
        >
          {children}
        </DrawerPrimitive.Content>
      </DrawerPortal>
    );
  },
);
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  children,
  showCloseButton,
  ...props
}: DrawerHeaderProps) => (
  <div
    className={cn("tw-border-b tw-border-gray-400 tw-p-16", className)}
    {...props}
  >
    <div
      className={cn("relative", {
        "tw-pr-24": showCloseButton,
      })}
    >
      {children}
      {showCloseButton && (
        <DrawerClose className="tw-absolute tw-right-16 tw-top-16 tw-flex tw-items-center tw-justify-center">
          <X24 />
        </DrawerClose>
      )}
    </div>
  </div>
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({ className, ...props }: DrawerFooterProps) => (
  <div
    className={cn(
      "tw-py-12 tw-px-16 tw-border-t tw-border-gray-400",
      className,
    )}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = forwardRef<HTMLHeadingElement, DrawerTitleProps>(
  ({ className, ...props }, ref) => (
    <DrawerPrimitive.Title
      ref={ref}
      className={cn("tw-headline-2-m", className)}
      {...props}
    />
  ),
);
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = forwardRef<
  HTMLParagraphElement,
  DrawerDescriptionProps
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("tw-body-2 tw-text-gray-600", className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
